import { FC } from "react";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  createFilterOptions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getLocalizedTextByLocaleVersionTwo } from "../../../../utils/localized-text";
import { v4 as uuidv4 } from "uuid";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { userSBFilterValues } from "../../../../redux/slices/userGroups/userGroupsSliceMultipleTables";
import { UserData } from "../../../../redux/actions/users/userTypes";
import { OrgGeoPointData } from "../../../../redux/actions/locations/locationsTypes";

type UserUpdateInformationParams = {
  actions: any;
  locale: string;
  isUpdateLoading: boolean;
  userValuesUpdate: {
    role: string;
    site: number;
    sites: number[];
    actions: {
      allValuesSelected: boolean;
      valuesSelected: userSBFilterValues[];
    };
    copyOfActions: {
      valuesSelected: userSBFilterValues[];
    };
    contactEmail: string;
    phone: string;
    phoneCode: string;
  };
  setUserEditValues: (values: any) => void;
  selectedUser: UserData | null;
  isUpdateError: string;
  locations: OrgGeoPointData[];
  handleLocationChange: (event: SelectChangeEvent) => void;
  updateUser: (token: string, userId: string) => void;
  setShowPassword: (value: boolean) => void;
  selectUser: () => void;
  token: string;
};

const UserUpdateInformation: FC<UserUpdateInformationParams> = ({
  actions,
  locale,
  isUpdateLoading,
  userValuesUpdate,
  setUserEditValues,
  selectedUser,
  isUpdateError,
  locations,
  handleLocationChange,
  updateUser,
  setShowPassword,
  selectUser,
  token,
}) => {
  const [t] = useTranslation("global");
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const locationWithFilteredSharesLocations = locations?.filter(
    (values) => !values.shared,
  );

  const AutoCompleteSelectActions = () => {
    const loading = false;
    const actionOptions = actions?.data
      ?.map((c: any) => ({
        id: c.id,
        name: getLocalizedTextByLocaleVersionTwo(
          locale,
          c?.name?.localizedTexts,
        ),
        scenarioId: c.id,
        cerealType: getLocalizedTextByLocaleVersionTwo(
          locale,
          c.cerealType.text.localizedTexts,
        ),
      }))
      .filter((values: any) => values.name !== undefined);

    return (
      <div>
        <Autocomplete
          multiple
          disabled={loading || isUpdateLoading}
          options={actionOptions}
          getOptionLabel={(option) => option.name}
          filterOptions={(options, params) => {
            const filter = createFilterOptions();
            const filtered = filter(options, params as any);
            return [
              { name: t("Dashboard.setAllLocations"), id: "all" },
              ...filtered,
            ];
          }}
          disableCloseOnSelect
          value={userValuesUpdate.actions.valuesSelected as any}
          isOptionEqualToValue={(option, value) =>
            option.id === value.scenarioId
          }
          getOptionKey={(option) => option.id}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                key={option.id}
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={
                  option.id === "all"
                    ? !!(
                        userValuesUpdate.actions.valuesSelected.filter(
                          (x) => x.id !== undefined,
                        ).length === actionOptions.length
                      )
                    : selected
                }
              />
              <Tooltip title={option.id} placement="right">
                <div>{`${option.name} ${
                  option.id === "all" ? "" : `(${option.cerealType})`
                }`}</div>
              </Tooltip>
            </li>
          )}
          renderTags={(list) => {
            if (list.length === 1) {
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {list.map((value: any) => (
                    <Chip key={value.scenarioId} label={value.name} />
                  ))}
                </Box>
              );
            }

            if (list.length > 1) {
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  <Chip
                    sx={{
                      height: "auto",
                      "& .MuiChip-label": {
                        display: "block",
                        whiteSpace: "normal",
                      },
                    }}
                    label={`${list[0].name}, ${list[1]?.name}, ${
                      list.length >= 3
                        ? `+ ${list.length - 2} ${t("Common_options.others")}`
                        : ""
                    }  `}
                  />
                </Box>
              );
            }
          }}
          onChange={(event, newValue) => {
            if (newValue.find((option) => option.id === "all")) {
              return setUserEditValues({
                ...userValuesUpdate,
                actions: {
                  allValuesSelected: false,
                  valuesSelected:
                    userValuesUpdate.actions.valuesSelected.length ===
                    actionOptions.length
                      ? []
                      : actionOptions,
                },
              });
            }

            setUserEditValues({
              ...userValuesUpdate,
              actions: {
                allValuesSelected: false,
                valuesSelected: newValue,
              },
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              InputLabelProps={{
                shrink: true,
              }}
              label={t("Users_details.assigned_action")}
            />
          )}
        />
      </div>
    );
  };

  return (
    <div className="modal-detail-user-container">
      <div>
        <div className="username-title-modal">
          {`${t("Common_options.Edit")} ${selectedUser?.username}`}
        </div>
      </div>
      <div>
        <div className="access-title">{t("Users_details.access_perm")}</div>
        <div>
          {isUpdateError ? <Alert severity="error">{isUpdateError}</Alert> : ""}
        </div>
        <div className="access-perm-info">
          <div className="access-values-users spacer-bottom"></div>
          {(selectedUser as any)?.role === "site" ||
          (selectedUser as any)?.role === "real" ? (
            <div className="access-values-users spacer-bottom">
              <div className="option-left-update mt-2">
                <FormControl fullWidth>
                  <InputLabel>{t("Users_details.assigned_site")}</InputLabel>
                  <Select
                    label={t("Users_details.assigned_site")}
                    value={userValuesUpdate?.site as any}
                    onChange={handleLocationChange}
                    disabled={isUpdateLoading}
                    MenuProps={{
                      autoFocus: false,
                      disableAutoFocusItem: true,
                      disableEnforceFocus: true,
                      disableAutoFocus: true,
                    }}
                  >
                    {locationWithFilteredSharesLocations.map((location) => {
                      return (
                        <MenuItem
                          key={`${location.name}${uuidv4()}`}
                          value={location.id}
                        >
                          {location.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </div>
            </div>
          ) : (
            ""
          )}

          <div className="auto-complete-actions-user spacer-bottom">
            <div className="option-left-update-autocomplete mt-2">
              {AutoCompleteSelectActions()}
            </div>
          </div>

          <div className="access-title">{t("Users_details.login_info")}</div>
          <div className="access-values-users spacer-bottom">
            <div className="option-left-update mt-2">
              <TextField
                label={t("Users_details.email")}
                variant="outlined"
                type="email"
                size="medium"
                value={userValuesUpdate.contactEmail}
                sx={{ width: "70%" }}
                disabled={isUpdateLoading}
                onChange={(event) =>
                  setUserEditValues({
                    ...userValuesUpdate,
                    contactEmail: event.target.value,
                  })
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="buttons-user-view">
        <div className="container-buttons-users-actions">
          <div></div>
          <div>
            <div>
              <Button
                variant="text"
                onClick={() => {
                  selectUser();
                  setShowPassword(false);
                }}
                disabled={isUpdateLoading}
                sx={{ textTransform: "none", marginRight: "2px" }}
              >
                {t("Common_options.Cancel")}
              </Button>

              <LoadingButton
                loading={isUpdateLoading}
                onClick={() => {
                  updateUser(token, selectedUser!.id);
                  setShowPassword(false);
                }}
                sx={{ textTransform: "none" }}
                variant="contained"
              >
                {t("Common_options.save_close")}
              </LoadingButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserUpdateInformation;
